import { Typography, Button } from "@monolith-forensics/monolith-ui";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import InquiriesAPI from "../../api/inquiries";
import Loader from "../../components/Loader";
import InquiryOverview from "./InquiryOverview";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import TaskButton from "../../Monolith-UI/TaskButton.js";
import KeyboardReturnOutlinedIcon from "@mui/icons-material/KeyboardReturnOutlined";
import ComboButton from "../../Monolith-UI/ComboButton/ComboButton.js";
import { usePermissions } from "../../hooks/usePermissions";
import { Inquiry } from "./InquirySection";
import { useTheme } from "styled-components";
import { MergeInquiryModal, ConvertInquiryModal } from "@/Features";

export const InquiryStatusOptions = [
  "New",
  "Contacted",
  "Converted",
  "Merged",
  "Transferred",
  "Accepted",
  "Declined",
];

const InquiryDetails = () => {
  const { currentUser } = usePermissions();
  const { inquiry_id } = useParams();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const theme: any = useTheme();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();

  const { data: inquiryInfo } = useQuery({
    queryKey: [
      "inquiries:list",
      { inquiry_id: inquiry_id ? parseInt(inquiry_id) : null },
    ],
    queryFn: () => InquiriesAPI.getInquiries({ inquiry_id }),
    select: (data) => data[0],
    refetchOnWindowFocus: true,
  });

  const handleInquiryUpdate = () => {
    queryClient.refetchQueries({
      queryKey: [
        "inquiries:list",
        { inquiry_id: inquiry_id ? parseInt(inquiry_id) : null },
      ],
    });
  };

  const handleStatusUpdate = (newStatus: string) => {
    queryClient.setQueryData(
      ["inquiries:list", { inquiry_id: inquiryInfo.inquiry_id }],
      (oldData: Inquiry[]) => {
        if (oldData[0]) return [{ ...oldData[0], inquiry_status: newStatus }];
      }
    );
    InquiriesAPI.updateInquiry(inquiry_id, { inquiry_status: newStatus });
  };

  return (
    <>
      {!inquiryInfo && <Loader />}
      {inquiryInfo && (
        <>
          <Helmet>
            <title>Inquiry Details | {String(inquiryInfo.inquiry_id)}</title>
          </Helmet>
          <div>
            <div
              style={{
                display: "flex",
                alignContent: "center",
                alignItems: "center",
              }}
            >
              {/*@ts-ignore*/}
              <Link to={-1}>
                <TaskButton
                  style={{
                    outline: `1px solid ${theme.palette.divider}`,
                    marginRight: 10,
                  }}
                  title={"Back to Inquiries"}
                >
                  <KeyboardReturnOutlinedIcon style={{ fontSize: 20 }} />
                </TaskButton>
              </Link>
              <div>
                <Typography variant="h3" display="inline">
                  Inquiry Details
                </Typography>
                <div style={{ color: theme.palette.text.secondary }}>
                  ID {inquiryInfo.inquiry_id}
                  {inquiryInfo.request_name
                    ? ` | ${inquiryInfo.request_name}`
                    : ""}
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 5,
                alignItems: "center",
                margin: "10px 0px",
              }}
            >
              {!inquiryInfo.case_id && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    size="xxs"
                    onClick={() => {
                      setShowCreateModal(true);
                    }}
                    disabled={
                      !hasPermission(MONOLITH_PERMISSIONS.INQUIRIES_UPDATE)
                    }
                  >
                    + Create Case
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="xxs"
                    onClick={() => {
                      setShowMergeModal(true);
                    }}
                    disabled={
                      !hasPermission(MONOLITH_PERMISSIONS.INQUIRIES_UPDATE)
                    }
                  >
                    {"Merge Inquiry"}
                  </Button>
                </>
              )}
              <ComboButton
                selectedItems={undefined}
                type="dropDown"
                data={InquiryStatusOptions}
                enabled={hasPermission(MONOLITH_PERMISSIONS.INQUIRIES_UPDATE)}
                value={inquiryInfo.inquiry_status}
                title={"Set Inquiry Status"}
                showDropdownIcon={true}
                variant="outlined"
                useSelectMode={true}
                onItemSelect={(e) => handleStatusUpdate(e)}
                dropDownTitle={() => {
                  return (
                    <div
                      style={{
                        margin: "5px 0px",
                        padding: 3,
                        color: theme.palette.text.secondary,
                        display: "flex",
                        alignItems: "center",
                        minWidth: 200,
                      }}
                    >
                      Set Inquiry Status
                    </div>
                  );
                }}
              >
                <></>
              </ComboButton>
            </div>
          </div>
          <InquiryOverview details={inquiryInfo} />
          {showCreateModal && (
            <ConvertInquiryModal
              inquiry={inquiryInfo}
              show={showCreateModal}
              onClose={() => setShowCreateModal(false)}
              onSubmit={() => handleInquiryUpdate()}
              defaultFormData={{
                case_type: inquiryInfo.inquiry_type,
                case_lead: {
                  label: currentUser.full_name,
                  value: currentUser.user_id,
                  data: currentUser,
                },
                case_description: inquiryInfo?.inquiry_case_num
                  ? `Client Ref Number: ${inquiryInfo?.inquiry_case_num}\n\n${inquiryInfo?.inquiry_notes}`
                  : inquiryInfo?.inquiry_notes,
              }}
            />
          )}
          {showMergeModal && (
            <MergeInquiryModal
              inquiry={inquiryInfo}
              show={showMergeModal}
              onClose={() => setShowMergeModal(false)}
              onSubmit={() => handleInquiryUpdate()}
              onCancel={() => setShowMergeModal(false)}
              defaultFormData={{ inquiry_evidence: inquiryInfo.evidence }}
            />
          )}
        </>
      )}
    </>
  );
};

export default InquiryDetails;
